import React, { useState, useRef } from "react";
import {
  Download,
  Upload,
  Database,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  HardDrive,
  RefreshCw,
  Shield,
  Archive,
  Calendar,
  Users,
  DollarSign,
  Scale,
  Megaphone,
  X,
} from "lucide-react";
import { useDataManagement } from "../context/DataManagementContext";
import { useData } from "../context/DataContext";
import { useFiles } from "../context/FileContext";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Badge from "../components/ui/Badge";
import { format } from "date-fns";

export default function DataManagement() {
  const {
    state,
    exportData,
    importData,
    createBackup,
    clearAllData,
    clearError,
  } = useDataManagement();
  const { state: dataState } = useData();
  const { state: fileState } = useFiles();
  const [selectedFormat, setSelectedFormat] = useState<
    "json" | "csv" | "excel"
  >("json");
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    try {
      await exportData(selectedFormat);
    } catch (error) {
      // Export failed
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        await importData(file);
      } catch (error) {
        // Import failed
      }
    }
  };

  const handleBackup = async () => {
    try {
      await createBackup();
    } catch (error) {
      // Backup failed
    }
  };

  const handleClearData = async () => {
    try {
      await clearAllData();
      setShowClearConfirm(false);
    } catch (error) {
      // Clear data failed
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getStorageUsage = () => {
    let totalSize = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length;
      }
    }
    return totalSize;
  };

  const dataStats = [
    {
      title: "Members",
      count: dataState.members.length,
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      title: "Files",
      count: fileState.files.length,
      icon: FileText,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      title: "Meetings",
      count: dataState.meetings.length,
      icon: Calendar,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      title: "Cases",
      count: dataState.cases.length,
      icon: Scale,
      color: "text-orange-600",
      bgColor: "bg-orange-100",
    },
    {
      title: "Financial Records",
      count: dataState.financialRecords.length,
      icon: DollarSign,
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
    },
    {
      title: "Events",
      count: dataState.events.length,
      icon: Megaphone,
      color: "text-pink-600",
      bgColor: "bg-pink-100",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Data Management</h1>
        <p className="text-gray-600">
          Export, import, and manage your syndicate data
        </p>
      </div>

      {/* Error Message */}
      {state.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{state.error}</p>
            <button
              onClick={clearError}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Data Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dataStats.map((stat, index) => (
          <Card key={index}>
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Storage Information */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Storage Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center">
            <div className="p-3 bg-gray-100 rounded-lg">
              <HardDrive className="h-6 w-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Storage Used</p>
              <p className="text-lg font-bold text-gray-900">
                {formatFileSize(getStorageUsage())}
              </p>
            </div>
          </div>
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Database className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Data Records</p>
              <p className="text-lg font-bold text-gray-900">
                {dataStats.reduce((sum, stat) => sum + stat.count, 0)}
              </p>
            </div>
          </div>
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <Archive className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Last Backup</p>
              <p className="text-lg font-bold text-gray-900">
                {state.lastBackup
                  ? format(new Date(state.lastBackup), "MMM dd, yyyy")
                  : "Never"}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Export Section */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Export Data
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Export Format
            </label>
            <div className="grid grid-cols-3 gap-4">
              {[
                {
                  value: "json",
                  label: "JSON",
                  description: "Complete data with structure",
                },
                {
                  value: "csv",
                  label: "CSV",
                  description: "Spreadsheet compatible",
                },
                {
                  value: "excel",
                  label: "Excel",
                  description: "Microsoft Excel format",
                },
              ].map((format) => (
                <button
                  key={format.value}
                  onClick={() => setSelectedFormat(format.value as any)}
                  className={`p-4 border rounded-lg text-left transition-colors ${
                    selectedFormat === format.value
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <div className="font-medium text-gray-900">
                    {format.label}
                  </div>
                  <div className="text-sm text-gray-500">
                    {format.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <Button
              onClick={handleExport}
              disabled={state.isExporting}
              className="flex-1"
            >
              {state.isExporting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Exporting... ({state.exportProgress}%)
                </div>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export Data
                </>
              )}
            </Button>
            <Button onClick={handleBackup} variant="outline" className="flex-1">
              <Archive className="h-4 w-4 mr-2" />
              Create Backup
            </Button>
          </div>

          {state.isExporting && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${state.exportProgress}%` }}
              ></div>
            </div>
          )}
        </div>
      </Card>

      {/* Import Section */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Import Data
        </h3>
        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              Import Data File
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Select a JSON file exported from this system
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={state.isImporting}
              variant="outline"
            >
              {state.isImporting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                  Importing... ({state.importProgress}%)
                </div>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Choose File
                </>
              )}
            </Button>
          </div>

          {state.isImporting && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${state.importProgress}%` }}
              ></div>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-800">Import Warning</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Importing data will replace all existing data. Make sure to
                  create a backup before importing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* System Management */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          System Management
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
            <div>
              <h4 className="font-medium text-red-800">Clear All Data</h4>
              <p className="text-sm text-red-700">
                This will permanently delete all data and files. This action
                cannot be undone.
              </p>
            </div>
            <Button
              onClick={() => setShowClearConfirm(true)}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-100"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Data
            </Button>
          </div>
        </div>
      </Card>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Clear All Data
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to clear all data? This will permanently
              delete:
            </p>
            <ul className="text-sm text-gray-600 mb-6 space-y-1">
              <li>• All member records</li>
              <li>• All files and documents</li>
              <li>• All meeting records</li>
              <li>• All financial data</li>
              <li>• All case records</li>
              <li>• All user accounts</li>
            </ul>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowClearConfirm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleClearData}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All Data
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
